import dayjs, { Dayjs } from 'dayjs'
import '@/utils/dayjs' // 确保 dayjs 插件已加载
import CONFIG from '../constants/config'

export const calcPhotosSize = (num = 0) => {
  let bytes = num * 200000
  if (bytes === 0) return '0 B'
  let k = 1000, // or 1024
    sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k))
  return (bytes / Math.pow(k, i)).toPrecision(3) + sizes[i]
}

/**
 *
 * @param size
 * @param decimalPlaces
 * @returns formatted storage size
 */
export const formatStorageSize = (
  size: number = 0,
  decimalPlaces: number = 2
): any => {
  if (size < 1024) {
    return {
      size: size,
      unit: 'B'
    }
  } else if (size < 1024 * 1024) {
    if (decimalPlaces === 0) {
      return {
        size: Math.round(size / 1024),
        unit: 'KB'
      }
    }
    return {
      size: (size / 1024).toFixed(decimalPlaces),
      unit: 'KB'
    }
  } else if (size < 1024 * 1024 * 1024) {
    if (decimalPlaces === 0) {
      return {
        size: Math.round(size / (1024 * 1024)),
        unit: 'MB'
      }
    }
    return {
      size: (size / (1024 * 1024)).toFixed(decimalPlaces),
      unit: 'MB'
    }
  } else {
    if (decimalPlaces === 0) {
      return {
        size: Math.round(size / (1024 * 1024 * 1024)),
        unit: 'GB'
      }
    }
    return {
      size: (size / (1024 * 1024 * 1024)).toFixed(decimalPlaces),
      unit: 'GB'
    }
  }
}

export const formatNum = (num: number = 0): string => {
  let resultArray: string[] = []
  let symbol: string = num < 0 ? '-' : ''
  let reversedArray: string[] = String(Math.abs(num)).split('').reverse()
  for (let i = 0; i < reversedArray.length; i += 3) {
    resultArray = resultArray.concat(reversedArray.slice(i, i + 3))
    if (i + 3 < reversedArray.length) {
      resultArray.push(',')
    }
  }
  return symbol + resultArray.reverse().join('')
}

const replacer = (match) => {
  return String.fromCodePoint(Number('0x' + match.substr(3, 5)))
}

export const parseStringWithUnicode = (str) => {
  return str.replace(/[[]\\u\w{5}]/g, replacer)
}

export const weekDay = (date: Dayjs): string => {
  // dayjs.day() 返回 0=周日, 1=周一, ..., 6=周六
  switch (date.day()) {
    case 1:
      return '星期一'
    case 2:
      return '星期二'
    case 3:
      return '星期三'
    case 4:
      return '星期四'
    case 5:
      return '星期五'
    case 6:
      return '星期六'
    case 0:
      return '星期日'
    default:
      return ''
  }
}

// 照片命名结果类型
interface PhotoNamingResult {
  seq: string
  photo_name: string
  photo_file: string
  hd: string
}

const photosDuplicateName = (photos: PhotoNamingResult[]): PhotoNamingResult[] => {
  let duplicateNames: Record<string, number> = {}
  return photos.map((photo) => {
    let originalNameParts = photo.photo_name.split('.')
    if (duplicateNames[photo.photo_name] !== undefined) {
      duplicateNames[photo.photo_name] += 1
      originalNameParts[0] += `（${duplicateNames[photo.photo_name]}）`
    } else {
      duplicateNames[photo.photo_name] = 0
      if (originalNameParts[0] === '') {
        originalNameParts[0] = '（0）'
      }
      if (originalNameParts[0].endsWith('/')) {
        originalNameParts[0] += '（0）'
      }
    }
    return { ...photo, photo_name: originalNameParts.join('.') }
  })
}

export const filterIllegalCharacters = (
  basename: string,
  exceptSymbols: string[] = []
): string => {
  let nameStrArr: string[] = Array.from(basename)
  let invalidSymbols: string[] = ['\\', '/', ':', '*', '?', '<', '>', '"', '|']
  // 去除exceptSymbols中的字符
  invalidSymbols = invalidSymbols.filter(
    (symbol) => !exceptSymbols.includes(symbol)
  )
  let filteredStr = nameStrArr.filter(
    (str) =>
      new RegExp(
        /[\u4e00-\u9fa5]+|[\u0020-\u007f]+|（|）|《|》|[|]|{|}|【|】|.|。|@|#|￥|%|……|&|·|~|、|；|;|,/g
      ).test(str) && !invalidSymbols.includes(str)
  )
  return filteredStr.join('')
}

export const namingAllPhotos = (photos: any[], folderRule: string, namingRule: string[]): PhotoNamingResult[] => {
  let folderCounts: Record<string, string[]> = {}
  let photoList = photos.map((photo) => {
    let folderName = ''
    let nameParts = namingRule.map((rule) => {
      switch (rule) {
        case 'user':
          return photo.author
        case 'time':
          return dayjs(photo.photo_time).format(
            CONFIG.photoNamingTimeFormat
          )
        case 'address':
          return photo.photo_location
        default:
          return ''
      }
    })

    if (folderRule === 'date') {
      folderName = dayjs(photo.photo_time).format(CONFIG.dateFormat)
      folderName += '/'
    }

    if (folderRule === 'user') {
      const nickname =
        filterIllegalCharacters(photo.author) || '未获取到拍摄人姓名'
      if (folderCounts[nickname]) {
        const index = folderCounts[nickname].indexOf(photo.user_id)
        if (index > -1) {
          folderName = index === 0 ? nickname : nickname + `（${index}）`
        } else {
          folderName = nickname + `（${folderCounts[nickname].length}）`
          folderCounts[nickname].push(photo.user_id)
        }
      } else {
        folderName = nickname
        folderCounts[nickname] = [photo.user_id]
      }
      folderName += '/'
    }

    return {
      seq: photo.seq,
      photo_name:
        folderName +
        filterIllegalCharacters(nameParts.join(' ')) +
        '.' +
        photo.photo_file.split('.')[1],
      photo_file: photo.photo_file,
      hd: photo.hd
    }
  })
  return photosDuplicateName(photoList)
}

export const namingSearchPhotos = (photos: any[], folderRule: string, namingRule: string[]): PhotoNamingResult[] => {
  let folderCounts: Record<string, string[]> = {}
  let photoList = photos.map((photo) => {
    let folderName = ''
    let nameParts = namingRule.map((rule) => {
      switch (rule) {
        case 'user':
          return photo.nickname
        case 'time':
          return dayjs(photo.time).format(CONFIG.photoNamingTimeFormat)
        case 'address':
          return photo.addr
        default:
          return ''
      }
    })

    if (folderRule === 'date') {
      folderName = dayjs(photo.photo_time).format(CONFIG.dateFormat)
      folderName += '/'
    }

    if (folderRule === 'user') {
      const nickname =
        filterIllegalCharacters(photo.nickname) || '未获取到拍摄人姓名'
      if (folderCounts[nickname]) {
        const index = folderCounts[nickname].indexOf(photo.user_id)
        if (index > -1) {
          folderName = index === 0 ? nickname : nickname + `（${index}）`
        } else {
          folderName = nickname + `（${folderCounts[nickname].length}）`
          folderCounts[nickname].push(photo.user_id)
        }
      } else {
        folderName = nickname
        folderCounts[nickname] = [photo.user_id]
      }
      folderName += '/'
    }

    return {
      seq: photo.seq,
      photo_name:
        folderName +
        filterIllegalCharacters(nameParts.join(' ')) +
        '.' +
        photo.photo_file.split('.')[1],
      photo_file: photo.photo_file,
      hd: photo.hd
    }
  })
  return photosDuplicateName(photoList)
}

export const raplaceEmoji = (str: string): string => {
  return str.replace(
    new RegExp(
      /[\u20e3\u203c\u2049\u2122\u2139\u231a\u2b1b\u2b1c\u2b50\u3030\u303d\u3297\u2b55\u3299\u231b\u2328\u23cf\u23e9\u24c2\u25aa\u25ab\u25B6\u25c0\u2b06\u{1F22F}\u{1F250}\u{1F251}\u{1FAC0}\u{1FAC1}\u{1FAC2}\u{1F17E}\u{1F17F}\u{1F18E}\u{1F201}\u{1F202}\u{1F21A}\u{1F171}\u{1F170}\u{1F004}\u{1F0CF}\u{a9}\u{ae}\u200d\u2934\u2935\uFFFD\u2B05\u2B07]|[\u{2194}-\u{21AA}]|[\u{23EA}-\u{23FA}]|[\u{25FB}-\u{25FE}]|[\u{2600}-\u{27BF}]|[\u{FE00}-\u{FE0F}]|[\u{1F191}-\u{1F19A}]|[\u{1F1E6}-\u{1F1FF}]|[\u{1F232}-\u{1F23A}]|[\u{1F300}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F7E0}-\u{1F7EB}]|[\u{1FA70}-\u{1FA74}]|[\u{1FA78}-\u{1FA86}]|[\u{1FA90}-\u{1FAA8}]|[\u{1FAB0}-\u{1FAB6}]|[\u{1F900}-\u{1F9FF}]|[\u{1FAD0}-\u{1FAD6}]|[\u{E0020}-\u{E007F}]/giu
    ),
    ''
  )
}

export function calcCharactersLen(str: string): number
export function calcCharactersLen(str: string, length?: number): boolean
export function calcCharactersLen(
  str: string,
  length?: number
): number | boolean {
  let double: string[] | null = str.match(new RegExp(/[\u007e-\u9fa5]+/g)) //ASCII 127+ 到 常用中文尾
  if (length || length === 0) {
    return str.length + (double ? double.join('').length : 0) <= length
  } else {
    return str.length + (double ? double.join('').length : 0)
  }
}

export function calcCharactersLength(str: string): number
/**
 *
 * @param str
 * @param length 字符数（1中文=2字符）
 */
export function calcCharactersLength(str: string, length?: number): boolean
export function calcCharactersLength(
  str: string,
  length?: number
): number | boolean {
  let chinese: string[] | null = str.match(new RegExp(/[\u4e00-\u9fa5]+/g)) //常用中文
  if (length || length === 0) {
    return str.length + (chinese ? chinese.join('').length : 0) <= length
  } else {
    return str.length + (chinese ? chinese.join('').length : 0)
  }
}

export const sliceCharactersLen = (str: string, limit: number) => {
  let res: string = ''
  let len: number = 0
  let index: number = 0

  let arr: string[] = str.split('')
  while (len < limit && index < arr.length) {
    if (/[\u007e-\u9fa5]+/g.test(arr[index])) len += 2
    else len += 1
    res += arr[index]
    index++
  }
  const result: null | string[] = res.match(new RegExp(/[\u007e-\u9fa5]+/g))
  const chinese: null | number = result === null ? 0 : result.join('').length
  if (chinese + res.length > limit) {
    res = res.slice(0, -1)
  }
  return res
}

/**
 *
 * @param str
 * @param length 允许的最大中文字数
 * @returns
 */
export const subWatermarkText = (str: string, length: number): string => {
  const chineseCharReg: RegExp | string = new RegExp(/[\u4e00-\u9fa5]/)
  const words: string[] = str.split('')
  let result: Array<string> = [],
    count: number = 0
  for (let index = 0; index < words.length; index++) {
    const word = words[index]
    if (chineseCharReg.test(word)) {
      count += 2
    } else {
      count += 1
    }
    if (count <= length * 2) {
      result.push(word)
    }
  }
  return result.join('')
}

export const loadScript = (src: string, cb: any): void => {
  let head: any = document.head || document.getElementsByTagName('head')[0]
  let script: any = document.createElement('script')

  cb = cb || function () {}

  script.type = 'text/javascript'
  script.src = src

  if (!('onload' in script)) {
    script.onreadystatechange = function () {
      if (this.readyState !== 'complete' && this.readyState !== 'loaded') return
      this.onreadystatechange = null
      cb(script)
    }
  }

  script.onload = function () {
    this.onload = null
    cb(script)
  }

  head.appendChild(script)
}

interface ieDocument extends Document {
  createEventObject: () => {}
}

export const fireKeyEvent = (el, evtType, keyCode) => {
  let evtObj
  if (document.createEvent) {
    if (window.hasOwnProperty('KeyEvent')) {
      //firefox 浏览器下模拟事件
      evtObj = document.createEvent('KeyEvents')
      evtObj.initKeyEvent(
        evtType,
        true,
        true,
        window,
        true,
        false,
        false,
        false,
        keyCode,
        0
      )
    } else {
      //chrome 浏览器下模拟事件
      evtObj = document.createEvent('UIEvents')
      evtObj.initUIEvent(evtType, true, true, window, 1)

      delete evtObj.keyCode
      if (typeof evtObj.keyCode === 'undefined') {
        //为了模拟keycode
        Object.defineProperty(evtObj, 'keyCode', { value: keyCode })
      } else {
        evtObj.key = String.fromCharCode(keyCode)
      }

      if (typeof evtObj.ctrlKey === 'undefined') {
        //为了模拟ctrl键
        Object.defineProperty(evtObj, 'ctrlKey', { value: true })
      } else {
        evtObj.ctrlKey = true
      }
    }
    el.dispatchEvent(evtObj)
  } else if (document.hasOwnProperty('createEventObject')) {
    //IE 浏览器下模拟事件
    evtObj = (document as ieDocument).createEventObject()
    evtObj.keyCode = keyCode
    el.fireEvent('on' + evtType, evtObj)
  }
}

export const tCaptcha = () => {
  return new Promise((resolve, reject) => {
    const createTaptcha = () => {
      let tencentCaptcha = new (window as any).TencentCaptcha(
        '196099232',
        (res) => {
          if (res.ret === 0) {
            resolve(res)
          } else {
            reject(res)
          }
        }
      )
      tencentCaptcha.show()
    }
    if ((window as any).TencentCaptcha === undefined) {
      loadScript('/TCaptcha.js', (script) => {
        createTaptcha()
      })
    } else {
      createTaptcha()
    }
  })
}

/**
 * 防抖函数，一段时间内只执行第一次或者最后一次
 */
export const debounce = <T extends (...args: any) => any>(
  fn: T,
  timeout = 200,
  immediate = false
) => {
  let timer: number | null
  return function (...args: any) {
    if (immediate && !timer) {
      fn && fn.apply(undefined, args)
      // return
    }
    if (timer) {
      window.clearTimeout(timer)
      timer = null
    }
    timer = window.setTimeout(() => {
      !immediate && fn && fn.apply(undefined, args)
      timer = null
    }, timeout)
  }
}
